<template>
  <div>
    <alert
      v-if="alertVisible"
      :message="alertMessage"
      :type="alertType">
    </alert>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron
                  @click="$router.go(-1)"/>
                {{ getTitle }}</h1>
              <div class="header-add-button">
                <span
                  v-tooltip.left.top-start="$t('permission.addUserGroup')">
                  <font-awesome-icon
                    class="header-add-button-icon"
                    @click="openAddPermissionModal"
                    :icon="icon.addbutton"/>
                </span>
              </div>
            </div>
          </grid-col>
        </grid-row>
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="permissionTable"
              :data="fetchData"
              sort-by="name"
              sort-order="asc"
              :show-caption="false"
              show-filter=false
              class="datatable"
              filter-placeholder=""
              filterNoResults="">
              <table-column
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    :icon="row.value.type === 'group' ? icon.groupIcon : icon.userIcon"/>
                </template>
              </table-column>
              <table-column
                show="label"
                header-class="text-left"
                cell-class="table-component__text-left"
                :label="$t('permission.name')">
              </table-column>
              <table-column
                show="row.permissions.canRead"
                :sortable="false"
                :filterable="false"
                header-class="table-component__icon__width__column"
                cell-class="text-center"
                :label="$t('permission.read')">
                <template slot-scope="row">
                  <simple-checkbox
                    :checked="true"
                    check-size="l"
                    class="table-iconset-icon checked table-icon-disabled"/>
                </template>
              </table-column>
              <table-column
                show="row.permissions.canEdit"
                :sortable="false"
                :filterable="false"
                header-class="table-component__icon__width__column"
                cell-class="text-center"
                :label="$t('permission.edit')">
                <template slot-scope="row">
                  <simple-checkbox
                    :id="'edit_' + row.value.type + row.value.id"
                    check-size="l"
                    :checked="row.permissions.canEdit"
                    v-on:change="onEditPermissionClick($event, row)"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
              <table-column
                show="row.permissions.canShare"
                :sortable="false"
                :filterable="false"
                header-class="table-component__icon__width__column"
                cell-class="text-center"
                :label="$t('permission.share')">
                <template slot-scope="row">
                  <simple-checkbox
                    :id="'share_' + row.value.type + row.value.id"
                    check-size="l"
                    :checked="row.permissions.canShare"
                    v-on:change="onSharePermissionClick($event, row)"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    :icon="icon.removePermission"
                    @click="onRemovePermission(row)"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
            </table-component>
          </grid-col>
        </grid-row>
      </div>
    </main-layout>
    <add-permission-modal
      v-if="addPermissionModal"
      :targets="getItemsWithoutPermissions"
      v-on:addPermission="onAddPermission"
      :errorMessage="$t('permission.errorNoPermissionSelected')"
      @cancel="addPermissionModal=false">
    </add-permission-modal>
    <ok-cancel-modal
      v-if="showConfirmationDeleteModal"
      v-on:ok="onRemovePermissionConfirm"
      v-on:cancel="onRemovePermissionCancel"
      :title="deleteModalMessage()"
      :okButton="$t('commons.delete')"
      :cancelButton="$t('commons.cancel')">
    </ok-cancel-modal>
    <ok-cancel-modal
      v-if="showOverlapPermissionModal"
      v-on:ok="onOverlapPermissionModalOk"
      v-on:cancel="showOverlapPermissionModal = false"
      :title="$t('permission.overlapTitle')"
      :okButton="$t('commons.accept')"
      :cancelButton="$t('commons.cancel')">
      <div slot="body">
        <ul>
          <li v-for="user in overlapedUsers" :key="user">
            {{ user }}
          </li>
        </ul>
        <h3>{{ $t('permission.overlapFooter') }}</h3>
        <ul></ul>
      </div>
    </ok-cancel-modal>
  </div>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import Alert from '@/components/shared/alert/Alert';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faTrashAlt,
  faUser,
  faUsers,
  faSquare
} from '@fortawesome/pro-regular-svg-icons';
import {faPlusCircle,
  faCheckSquare} from '@fortawesome/pro-solid-svg-icons';
import BtnPill from '../../components/shared/buttons/BtnPill';
import Checkbox from '../../components/shared/forms/Checkbox';
import AddPermissionModal from '../../components/permissions/AddPermissionModal';
import {mapActions, mapGetters} from 'vuex';
import OkCancelModal from '../../components/shared/dialogs/OkCancelModal';
import pagination from '@/components/shared/table/pagination';
import SimpleCheckbox from '../../components/shared/forms/SimpleCheckbox';
import Chevron from '@/components/shared/Chevron';

export default {
  name: 'PermissionsPanel',
  components: {
    Alert,
    SimpleCheckbox,
    OkCancelModal,
    AddPermissionModal,
    Checkbox,
    MainLayout,
    BtnPill,
    FontAwesomeIcon,
    TableColumn,
    TableComponent,
    GridRow,
    GridCol,
    Chevron
  },
  data () {
    return {
      icon: {
        removePermission: faTrashAlt,
        addbutton: faPlusCircle,
        userIcon: faUser,
        groupIcon: faUsers,
        nonChecked: faSquare,
        checked: faCheckSquare
      },
      titleKey: '',
      deletingPermission: undefined,
      showConfirmationDeleteModal: false,
      addPermissionModal: false,
      breadcrumb: [],
      objectType: undefined,
      objectId: undefined,
      editingPermission: undefined,
      addingPermission: undefined,
      showOverlapPermissionModal: false,
      overlapedUsers: [],
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      alertVisible: false,
      alertMessage: '',
      alertType: '' // error, warning, success
    };
  },
  computed: {
    ...mapGetters([
      'getItemsWithoutPermissions',
      'getItemsWithPermissions'
    ]),
    getTitle () {
      return this.$t(this.titleKey);
    },
    getBreadcrumb () {
      return this.breadcrumb.map(route => {
        route.name = this.$t(route.nameKey);
        return route;
      }).concat(
        {
          name: this.$t(this.titleKey),
          link: '/' + this.objectType + '/' + this.objectId + '/permission/list',
          disabled: true
        });
    }
  },
  watch: {
    getItemsWithPermissions: {
      handler: function () {
        this.$refs.permissionTable.refresh();
      }
    },
    deep: true
  },
  created () {
    const params = this.$route.params;
    this.titleKey = params.titleKey;
    this.breadcrumb = params.breadcrumb || [];
    if (this.breadcrumb !== []) {
      this.breadcrumb[this.breadcrumb.length - 1].disabled = false;
    }
    this.objectType = params.type;
    this.objectId = params.id;
    this.retrievePermissionData({ context: this, objectId: this.objectId, objectType: this.objectType });
  },
  destroyed () {
    this.cleanState();
  },
  methods: {
    ...mapActions([
      'retrievePermissionData',
      'updatePermission',
      'addPermission',
      'removePermission',
      'cleanState'
    ]),
    fetchData ({ page, filter, sort }) {
      const tablePage = pagination.createPage(
        this.getItemsWithPermissions,
        this.tableState,
        ['label'],
        this.tablePageSize,
        { page, filter, sort });
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    onEditPermissionClick (checked, row) {
      if (!row) {
        return;
      }
      this.editingPermission = {
        context: this,
        permissionType: 'edit',
        permissionValue: checked,
        objectId: this.objectId,
        objectType: this.objectType,
        userOrGroup: row.value.type,
        userOrGroupId: row.value.id
      };
      if (checked && row.value.type === 'group') {
        const users = getUsersWithLessPermissions(this.getItemsWithPermissions, row, {canEdit: checked, canShare: row.permissions.canShare});
        if (users.length > 0) {
          this.openOverlapPermissionsModal(users);
          return;
        }
      }
      this.saveEditingPermission();
    },
    onSharePermissionClick (checked, row) {
      if (!row) {
        return;
      }
      this.editingPermission = {
        context: this,
        permissionType: 'share',
        permissionValue: checked,
        objectId: this.objectId,
        objectType: this.objectType,
        userOrGroup: row.value.type,
        userOrGroupId: row.value.id
      };
      if (checked && row.value.type === 'group') {
        const users = getUsersWithLessPermissions(this.getItemsWithPermissions, row, {canEdit: row.permissions.canEdit, canShare: checked});
        if (users.length > 0) {
          this.openOverlapPermissionsModal(users);
          return;
        }
      }
      this.saveEditingPermission();
    },
    onOverlapPermissionModalOk () {
      if (this.editingPermission) {
        this.saveEditingPermission();
      } else if (this.addingPermission) {
        this.createNewPermission();
      }
      this.closeOverlapPermissionsModal();
    },
    saveEditingPermission () {
      this.updatePermission(this.editingPermission);
      this.editingPermission = undefined;
    },
    onAddPermission (event) {
      this.addingPermission = {
        label: event.target.label,
        value: event.target.value,
        permissions: {
          canRead: true,
          canEdit: event.edit,
          canShare: event.share
        }
      };
      if (this.addingPermission.value.type === 'group') {
        const users = getUsersWithLessPermissions(this.getItemsWithPermissions, this.addingPermission, {canEdit: event.edit, canShare: event.share});
        if (users.length > 0) {
          this.openOverlapPermissionsModal(users);
          return;
        }
      }
      this.createNewPermission();
      this.addPermissionModal = false;
    },
    createNewPermission () {
      this.addPermissionRequest({
        context: this,
        objectId: this.objectId,
        objectType: this.objectType,
        item: this.addingPermission
      });
      this.addingPermission = undefined;
      this.closeAddPermissionModal();
    },
    addPermissionRequest (payload) {
      this.addPermission(payload).then(
        () => {
          showAlert(this, this.$t('permission.successAddPermission'), 'success');
        },
        () => {
          showAlert(this, this.$t('permission.errorAddPermission'), 'error');
        }
      );
    },
    onRemovePermission (target) {
      this.deletingPermission = target;
      this.showConfirmationDeleteModal = true;
    },
    onRemovePermissionConfirm () {
      this.removePermissionRequest({context: this, objectId: this.objectId, objectType: this.objectType, item: this.deletingPermission});
      this.deletingPermission = {};
      this.showConfirmationDeleteModal = false;
    },
    onRemovePermissionCancel () {
      this.deletingPermission = {};
      this.showConfirmationDeleteModal = false;
    },
    removePermissionRequest (payload) {
      this.removePermission(payload).then(
        () => {
          showAlert(this, this.$t('permission.successRemovePermission'), 'success');
        },
        () => {
          showAlert(this, this.$t('permission.errorRemovePermission'), 'error');
        }
      );
    },
    openOverlapPermissionsModal (users) {
      this.overlapedUsers = users;
      this.showOverlapPermissionModal = true;
    },
    closeOverlapPermissionsModal () {
      this.overlapedUsers = [];
      this.showOverlapPermissionModal = false;
    },
    openAddPermissionModal () {
      this.addPermissionModal = true;
    },
    closeAddPermissionModal () {
      this.addPermissionModal = false;
    },
    deleteModalMessage () {
      const itemType = this.deletingPermission.value.type;
      const itemTypeTranslation = this.$t('permission.' + itemType);
      return this.$t('permission.deleteModalMessage', {item: itemTypeTranslation});
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};

const getUsersWithLessPermissions = (itemsWithPermissions, groupItem, permissions) => {
  const users = [];
  for (let item of itemsWithPermissions) {
    if (item.value.type === 'user' &&
      item.value.groups.indexOf(groupItem.value.id) > -1 &&
      ((permissions.canEdit && !item.permissions.canEdit) ||
        (permissions.canShare && !item.permissions.canShare))) {
      users.push(item.value.name);
    }
  }
  return users;
};
</script>

<style lang="scss">
@import "src/stylesheets/variables";

.checked {
  font-size: $fs-l;
  color: $active;
}

.non_checked {
  font-size: $fs-l;
}

#header {
  display: inline-block;
  width: 100%;
  line-height: 1rem;
  h1 {
    margin-bottom: 0;
    margin-right: $sp-margin;
    padding-right: $sp-margin;
  }
}

.header-title {
  float: left;
}

.header-add-button {
  float: right;
  cursor: pointer;
}

.header-add-button-icon {
  font-size: 3rem;
  color: $secondary;
}
</style>



// WEBPACK FOOTER //
// src/layouts/groups/PermissionList.vue